<template>
  <section :class="style.username">
    <one-line-form :submit="updateUsername" :submittable="!!username && validUsername" :saving="isSaving ? username : ''" :loading="isSaving && this.screenParams.validation">
      <template slot="underlay">
        <transition name="fade">
          <div class="input">
            <ins v-if="showWarning">
              {{ username }}
              <span>
                {{ i18n.t('base:please_change') }}
              </span>
            </ins>
          </div>
        </transition>
      </template>
      <input-field name="username"
                   :class="{ invisible: isSaving, warning: showWarning }"
                   type="text"
                   :title="i18n.t('username:username')"
                   :hideLabel="showWarning"
                   :placeholder="i18n.t('username:username_placeholder')"
                   :autocapitalize="false"
                   :autocorrect="false"
                   :autocomplete="'username'"
                   :value="username"
                   :maxlength="usernameMaxLength"
                   @input="syncUsername"
                   :cleanRegExp="new RegExp(/\s+$/)"
                   v-keyboard="'alphanumeric'" />
    </one-line-form>
    <helper v-show="helperMessage && !showWarning" :error="!isSaving">{{ helperMessage }}</helper>
    <transition name="bottom" appear>
      <action-bar :hint="i18n.t('username:username_taken')" v-if="showWarning && showActions">
        <ul class="columns">
          <li @click="editUsername" class="primary">
            <span>{{ i18n.t('base:try_another') }}</span>
          </li>
        </ul>
      </action-bar>
    </transition>
    <transition name="bottom" appear>
      <div v-show="!showWarning">
        <numb-area></numb-area>
        <show-keyboard></show-keyboard>
      </div>
    </transition>
  </section>
</template>

<style lang="scss" rel="stylesheet/scss" scoped>
  @import '../assets/mixins';

  .loading-spinner{
    left: calc(100% - 40px)
  }

  ul {
    @include button-list;
  }

  ins {
    color: transparent;
    background: #FFE676;
    display: inline-block;
    position: relative;
    max-width: 100%;

    span {
      background: no-repeat left 9px;
      background-image: url('~static/warning_pointer.svg');
      position: absolute;
      display: block;
      left: 40px;
      bottom: 100%;
      height: 40px;
      color: $inputWarningColor;
      color: $inputWarningColor-portal;
      font-size: 1rem;
      font-weight: lighter;
      padding-left: 42px;
      margin-bottom: 10px;
      line-height: 120%;
    }
  }
</style>

<script>
  import Vue from 'vue'
  import { mapState } from 'vuex'

  import AdditionalKeyboard from '../components/AdditionalKeyboard'
  import Spinner from '../components/Spinner'
  import CharKeyboard from '../components/CharKeyboard'

  export default Vue.component('username-screen', {
    
    props: ['screenParams'],
    components: {
      'additional-keyboard': AdditionalKeyboard,
      'spinner': Spinner,
      'char-keyboard': CharKeyboard
    },
    data () {
      const credentials = this.$store.state.credentials
      return {
        usernameOld: '',
        username: credentials.username,
        isSaving: false,
        usernameMinLength: 6,
        usernameMaxLength: '14',
        showWarning: false,
        showActions: false,
        dataCheckCount: 0
      }
    },
    computed: {
      ...mapState(['app', 'dataStatus', 'partner']),
      style () {
        return {
          username: this.$css(`
          ins {
            span {
              color: ${this.theme.inputWarningColor} !important;
            }
          }
          `)
        }
      },
      validUsername () {
        let isOldUsername = this.screenParams.validation && this.username === this.usernameOld
        const hasUsernameError = (this.screenParams && this.screenParams.validation) && this.errorPresent('duplicateUsername')

        return this.usernameLengthValid() && !hasUsernameError && !isOldUsername && this.isUsernamContainValidCharacters()
      },
      helperMessage () {
        if (this.usernameLengthValid() && !this.isUsernamContainValidCharacters()) {
           return this.i18n.t('username:static_hint')
        }
  
        if (this.isSaving && this.screenParams.validation) {
          return this.i18n.t('base:verifying')
        }

        if (this.username && this.username.length < this.usernameMinLength) {
          return this.i18n.t('username:username_short')
        }

        if (this.errorPresent('duplicateUsername') && this.screenParams.validation) {
          return this.i18n.t('username:username_taken_long')
        }

        return ''
      }
    },
    watch: {
      showWarning () {
        if (this.showWarning && document.activeElement.nodeName.toLowerCase() === 'input') {
          document.activeElement.blur()
        }

        if (this.showWarning) {
          return this.$emit('errorScreen', true)
        }
        this.$emit('errorScreen', false)
      },
      username () {
        if (this.screenParams.validation) {
          this.$store.dispatch('deleteError', {
            errorName: 'duplicateUsername'
          })
        }
      }
    },
    created () {
      this.showWarning = this.screenParams.validation && !!this.errorPresent('duplicateUsername')
      this.usernameOld = this.username
    },
    mounted () {
      setTimeout(() => {
        this.showActions = true
      }, 500)

      this.catchEdit()
    },
    methods: {
      usernameLengthValid () {
        let usernameLength = this.username && this.username.length
        return usernameLength >= this.usernameMinLength
      },
      isUsernamContainValidCharacters () {
        const allowedCharactersReg = this.partner.usernameRules.allowedCharactersReg
        return allowedCharactersReg ? allowedCharactersReg.test(this.username) : true
      },
      syncUsername (value) {
        this.username = value
      },
      updateUsername () {
        if (this.username) {
          this.isSaving = true

          if (this.username !== this.usernameOld && this.errorPresent('duplicateUsername')) {
            this.$store.dispatch('deleteError', {
              errorName: 'duplicateUsername'
            })
          }

          if (this.screenParams.validation) {
            this.$store.commit('updateDataStatus', {
              checking: true
            })
          }

          this.$store.dispatch('updateCredentials', {
            username: this.username
          })

          if (this.screenParams.validation) {
            return this.dataLeave()
          }

          this.$store.dispatch('next', this.screenParams.nextScreenRoute)
        }
      },
      editUsername () {
        this.isSaving = false
        this.showWarning = false

        const input = this.$el.querySelector('input')

        if (input) {
          input.focus()
        }
      },
      errorPresent (errorSearchName) {
        const errors = this.dataStatus.errors && this.dataStatus.errors.length > 0 ? this.dataStatus.errors : false

        if (errors) {
          return errors.find((error) => {
            return error.name === errorSearchName
          })
        }
      },
      catchEdit () {
        const input = this.$el.querySelector('input')

        input.addEventListener('focus', () => {
          if (this.showWarning) this.editUsername()
        }, true)
      },
      dataLeave () {
        return setTimeout(() => {
          let checkingDone = !this.dataStatus.checking
          if ((checkingDone && !this.errorPresent('duplicateUsername')) || this.dataCheckCount === 10) {
            return this.$store.dispatch('next', this.screenParams.nextScreenRoute)
          } else if (checkingDone) {
            this.isSaving = false
            this.usernameOld = this.username
            return
          }
          this.dataCheckCount++
          return this.dataLeave()
        }, 500)
      }
    }
  })
</script>



// WEBPACK FOOTER //
// src/screens/CredentialsUsername.vue